import { Link, TableColumn } from '@backstage/core-components';
import type { Activity } from '../../api/types';
import {
  ActivityTitleCell,
  CreationCell,
  DestinationCell,
} from './ActivityTableCells';
import { ActivityIssueCell } from './ActivityIssueCell';
import { ActivityStatusCell, PhaseChip } from './PortfolioStatusCells';
import {
  PortfolioSelectionCell,
  PortfolioSelectionHeader,
} from './PortfolioSelectionCells';
import type { PortfolioSelection } from './usePortfolioSelection';
import type { PortfolioAccess } from './usePortfolioAccess';
import type { PortfolioRoutes } from './usePortfolioRoutes';
import type { PortfolioLabels } from './usePortfolioLabels';

export interface PortfolioColumnContext {
  selection: PortfolioSelection;
  access: PortfolioAccess;
  routes: PortfolioRoutes;
  labels: PortfolioLabels;
  busy: boolean;
}

function selectionColumn({
  selection,
  access,
  busy,
}: PortfolioColumnContext): TableColumn<Activity> {
  return {
    title: <PortfolioSelectionHeader selection={selection} busy={busy} />,
    field: 'selection',
    width: '48px',
    sorting: false,
    hidden: !access.isGlobalAdmin,
    render: row => (
      <PortfolioSelectionCell
        activity={row}
        selection={selection}
        busy={busy}
      />
    ),
  };
}

function activityColumns({
  access,
  routes,
}: PortfolioColumnContext): TableColumn<Activity>[] {
  return [
    {
      title: 'Título',
      field: 'title',
      highlight: true,
      width: '220px',
      render: row => (
        <ActivityTitleCell
          activity={row}
          locked={access.isActivityLockedForUser(row)}
          route={routes.getPrimaryActivityRoute(row)}
        />
      ),
    },
    {
      title: 'Issue',
      field: 'azureWorkItem',
      width: '68px',
      render: row => <ActivityIssueCell activity={row} />,
    },
    {
      title: 'Status',
      field: 'status',
      width: '95px',
      render: row => <ActivityStatusCell activity={row} />,
    },
    {
      title: 'Fase Atual',
      field: 'currentPhase',
      width: '90px',
      render: row => <PhaseChip phase={row.currentPhase} />,
    },
  ];
}

function contextColumns({
  labels,
  routes,
}: PortfolioColumnContext): TableColumn<Activity>[] {
  return [
    {
      title: 'Destino',
      field: 'azureProject',
      width: '170px',
      render: row => (
        <DestinationCell
          activity={row}
          projectLabel={labels.getProjectLabel(row.azureProject)}
        />
      ),
    },
    {
      title: 'Criação',
      field: 'createdAt',
      width: '145px',
      render: row => (
        <CreationCell
          activity={row}
          userLabel={labels.getUserLabel(row.userId)}
        />
      ),
    },
    {
      title: 'Cockpit',
      field: 'id',
      sorting: false,
      width: '72px',
      render: row => (
        <Link to={routes.getActivityCockpitRoute(row)}>Cockpit</Link>
      ),
    },
  ];
}

export function portfolioColumns(
  context: PortfolioColumnContext,
): TableColumn<Activity>[] {
  return [
    selectionColumn(context),
    ...activityColumns(context),
    ...contextColumns(context),
  ];
}
